import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FaPencilAlt, FaTrash, FaPlus } from 'react-icons/fa';
import dayjs from 'dayjs';
import { formatDateOnly, toEditFormDates, isSameDateOnly, fromPickerValue } from '../../../utils/dateOnly';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Title,
  CloseButton,
  HistoryList,
  HistoryItem,
  TimelineContainer,
  TimelineDot,
  TimelineLine,
  ManagerInfo,
  ManagerName,
  DateRange,
  CurrentBadge,
  ActionButtons,
  ActionButton,
  ContentToolbar,
  AddButton,
  FirstRowWrapper
} from './styles';
import { EmployeeAvatar } from '../EmployeeAvatar';
import EmptyState from '../EmptyState';
import LoadingSpinner from '../LoadingSpinner';
import Modal, { Field } from '../Modal';
import ConfirmationModal from '../ConfirmationModal';
import EmployeeSearch from '../EmployeeSearch';
import { organizationApi, projectsApi, teamsApi, managerHistoryApi } from '../../../services/api';
import { MANAGER_HISTORY_ENTITY_TYPES, MANAGER_HISTORY_API_TYPES, getEntityIdField, getManagerHistoryTerminology } from '../../../constants/managerHistory';
import { useAlert } from '../../../contexts/AlertContext';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';

const ManagerHistoryModal = ({
  isOpen,
  onClose,
  entityId,
  entityName,
  entityType,
  onUpdate,
  onCurrentManagerChange,
  readOnly = false // ADR-120: history of an ended unit is viewable, not editable
}) => {
  const { showAlert } = useAlert();
  const ability = useAbility();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Edit modal state
  const [editModal, setEditModal] = useState({
    open: false,
    item: null,
    manager: null,
    startDate: null,
    endDate: null,
    isSubmitting: false
  });

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    item: null,
    isDeleting: false
  });

  // Create modal state
  const [createModal, setCreateModal] = useState({
    open: false,
    manager: null,
    startDate: null,
    endDate: null,
    isSubmitting: false
  });

  const terminology = getManagerHistoryTerminology(entityType);
  const label = terminology === 'owner' ? 'Owner' : 'Representative';

  const canCreate = !readOnly && ability.can(ACTIONS.CREATE, SUBJECTS.MANAGER_HISTORY);
  const canUpdate = !readOnly && ability.can(ACTIONS.UPDATE, SUBJECTS.MANAGER_HISTORY);
  const canDelete = !readOnly && ability.can(ACTIONS.DELETE, SUBJECTS.MANAGER_HISTORY);

  const fetchHistory = async (notifyCurrentManager = false) => {
    setIsLoading(true);
    setError(null);
    try {
      let data;
      switch (entityType) {
        case MANAGER_HISTORY_ENTITY_TYPES.CLUSTER:
          data = await organizationApi.getClusterManagerHistory(entityId);
          break;
        case MANAGER_HISTORY_ENTITY_TYPES.BUSINESS_UNIT:
          data = await organizationApi.getBusinessUnitOwnerHistory(entityId);
          break;
        case MANAGER_HISTORY_ENTITY_TYPES.PROJECT:
          data = await projectsApi.getProjectManagerHistory(entityId);
          break;
        case MANAGER_HISTORY_ENTITY_TYPES.TEAM:
          data = await teamsApi.getTeamManagerHistory(entityId);
          break;
        default:
          data = [];
      }
      setHistory(data);

      // Notify parent of current manager change if requested
      if (notifyCurrentManager && onCurrentManagerChange) {
        const currentRecord = data.find(item => item.isCurrent);
        const currentManager = currentRecord?.manager || currentRecord?.owner || null;
        onCurrentManagerChange(currentManager);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !entityId || !entityType) return;
    fetchHistory();
  }, [isOpen, entityId, entityType]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHistory([]);
      setError(null);
      setEditModal({ open: false, item: null, manager: null, startDate: null, endDate: null, isSubmitting: false });
      setDeleteModal({ open: false, item: null, isDeleting: false });
      setCreateModal({ open: false, manager: null, startDate: null, endDate: null, isSubmitting: false });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ADR-197: start/end are date-only — render the stored calendar day
  const formatDate = (dateString) => formatDateOnly(dateString, 'MMM D, YYYY', 'Present');

  const sortedHistory = [...(history || [])].sort(
    (a, b) => new Date(b.startDate) - new Date(a.startDate)
  );

  // Edit handlers
  const handleOpenEdit = (item) => {
    const person = item.manager || item.owner;
    setEditModal({
      open: true,
      item,
      manager: person,
      ...toEditFormDates(item),
      isSubmitting: false
    });
  };

  const handleCloseEdit = () => {
    if (!editModal.isSubmitting) {
      setEditModal({ open: false, item: null, manager: null, startDate: null, endDate: null, isSubmitting: false });
    }
  };

  const handleEditSubmit = async () => {
    if (!editModal.item) return;

    setEditModal(prev => ({ ...prev, isSubmitting: true }));

    try {
      const originalPerson = editModal.item.manager || editModal.item.owner;
      const managerChanged = editModal.manager?.id !== originalPerson?.id;
      const startDateChanged = !isSameDateOnly(editModal.startDate, editModal.item.startDate);
      const endDateChanged = !isSameDateOnly(editModal.endDate, editModal.item.endDate);

      // Update manager if changed
      if (managerChanged && editModal.manager?.id) {
        await managerHistoryApi.updateManagerHistoryManager(editModal.item.id, editModal.manager.id);
      }

      // Update dates if changed
      if (startDateChanged || endDateChanged) {
        await managerHistoryApi.updateManagerHistoryDates(editModal.item.id, {
          startDate: fromPickerValue(editModal.startDate) ?? undefined,
          endDate: fromPickerValue(editModal.endDate)
        });
      }

      showAlert('Record updated successfully', 'success');
      handleCloseEdit();
      await fetchHistory(true);
      onUpdate?.();
    } catch (err) {
      showAlert(err.message || 'Failed to update record', 'error', err);
    } finally {
      setEditModal(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  // Delete handlers
  const handleOpenDelete = (item) => {
    setDeleteModal({ open: true, item, isDeleting: false });
  };

  const handleCloseDelete = () => {
    if (!deleteModal.isDeleting) {
      setDeleteModal({ open: false, item: null, isDeleting: false });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.item) return;

    setDeleteModal(prev => ({ ...prev, isDeleting: true }));

    try {
      await managerHistoryApi.deleteManagerHistory(deleteModal.item.id);
      showAlert('Record deleted successfully', 'success');
      handleCloseDelete();
      await fetchHistory(true);
      onUpdate?.();
    } catch (err) {
      showAlert(err.message || 'Failed to delete record', 'error', err);
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const deleteItemPerson = deleteModal.item?.manager || deleteModal.item?.owner;

  // Create handlers
  const handleOpenCreate = () => {
    setCreateModal({
      open: true,
      manager: null,
      startDate: dayjs(),
      endDate: null,
      isSubmitting: false
    });
  };

  const handleCloseCreate = () => {
    if (!createModal.isSubmitting) {
      setCreateModal({ open: false, manager: null, startDate: null, endDate: null, isSubmitting: false });
    }
  };

  const handleCreateSubmit = async () => {
    setCreateModal(prev => ({ ...prev, isSubmitting: true }));
    try {
      const apiType = MANAGER_HISTORY_API_TYPES[entityType];
      const entityIdField = getEntityIdField(entityType);

      await managerHistoryApi.createManagerHistory({
        managerId: createModal.manager.id,
        type: apiType,
        [entityIdField]: entityId,
        startDate: createModal.startDate.format('YYYY-MM-DD'),
        endDate: createModal.endDate?.format('YYYY-MM-DD') || undefined
      });

      showAlert(`${label} record created successfully`, 'success');
      handleCloseCreate();
      await fetchHistory(true);
      onUpdate?.();
    } catch (err) {
      showAlert(err.message || 'Failed to create record', 'error', err);
    } finally {
      setCreateModal(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title $terminology={terminology}>
            <span>{entityName}</span>
          </Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        {isLoading ? (
          <LoadingSpinner message={`Loading ${label.toLowerCase()} history...`} />
        ) : error ? (
          <EmptyState title="Error" description={error} />
        ) : sortedHistory.length === 0 ? (
          <>
            {canCreate && (
              <ContentToolbar>
                <AddButton onClick={handleOpenCreate}>
                  <FaPlus size={12} /> Add {label}
                </AddButton>
              </ContentToolbar>
            )}
            <EmptyState
              title={`No ${label} History`}
              description={`No ${label.toLowerCase()} history available for this ${entityType}.`}
            />
          </>
        ) : (
          <HistoryList>
            {sortedHistory.map((item, index) => {
              const person = item.manager || item.owner;
              const isFirstRow = index === 0;

              const rowContent = (
                <HistoryItem>
                  <TimelineContainer>
                    <TimelineDot $isCurrent={item.isCurrent} />
                    {index < sortedHistory.length - 1 && <TimelineLine />}
                  </TimelineContainer>
                  <ManagerInfo>
                    <EmployeeAvatar employee={person} size={40} />
                    <div>
                      <ManagerName>
                        {person?.fullName || 'Unknown'}
                        {item.isCurrent && <CurrentBadge>Current</CurrentBadge>}
                      </ManagerName>
                      <DateRange>
                        {formatDate(item.startDate)} - {formatDate(item.endDate)}
                      </DateRange>
                    </div>
                  </ManagerInfo>
                  {(canUpdate || canDelete) && (
                    <ActionButtons>
                      {canUpdate && (
                        <ActionButton
                          onClick={() => handleOpenEdit(item)}
                          title={`Edit ${label.toLowerCase()}`}
                        >
                          <FaPencilAlt size={14} />
                        </ActionButton>
                      )}
                      {canDelete && (
                        <ActionButton
                          $variant="delete"
                          onClick={() => handleOpenDelete(item)}
                          title="Delete record"
                        >
                          <FaTrash size={14} />
                        </ActionButton>
                      )}
                    </ActionButtons>
                  )}
                </HistoryItem>
              );

              if (isFirstRow && canCreate) {
                return (
                  <FirstRowWrapper key={item.id}>
                    {rowContent}
                    <ContentToolbar>
                      <AddButton onClick={handleOpenCreate}>
                        <FaPlus size={12} /> Add {label}
                      </AddButton>
                    </ContentToolbar>
                  </FirstRowWrapper>
                );
              }

              return <React.Fragment key={item.id}>{rowContent}</React.Fragment>;
            })}
          </HistoryList>
        )}

        {/* Edit Modal */}
        <Modal
          isOpen={editModal.open}
          onClose={handleCloseEdit}
          title={`Edit ${label} Record`}
          onSubmit={handleEditSubmit}
          submitText={editModal.isSubmitting ? 'Saving...' : 'Save'}
          submitDisabled={!editModal.manager || !editModal.startDate || editModal.isSubmitting}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Field>
              <EmployeeSearch
                value={editModal.manager}
                onChange={(employee) => setEditModal(prev => ({ ...prev, manager: employee }))}
                label={label}
                placeholder={`Search for ${label.toLowerCase()}...`}
                width={400}
                height="56px"
                disabled={editModal.isSubmitting}
                showSelectedAvatar={true}
              />
            </Field>

            <Field>
              <DatePicker
                label="Start Date"
                value={editModal.startDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                    setEditModal(prev => ({ ...prev, startDate: newValue }));
                  } else if (newValue === null) {
                    setEditModal(prev => ({ ...prev, startDate: null }));
                  }
                }}
                format="DD/MM/YYYY"
                disabled={editModal.isSubmitting}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    required: true
                  }
                }}
              />
            </Field>

            <Field>
              <DatePicker
                label="End Date"
                value={editModal.endDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                    setEditModal(prev => ({ ...prev, endDate: newValue }));
                  } else if (newValue === null) {
                    setEditModal(prev => ({ ...prev, endDate: null }));
                  }
                }}
                format="DD/MM/YYYY"
                disabled={editModal.isSubmitting}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small"
                  }
                }}
              />
              {editModal.endDate ? (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: 'pointer', mt: 1, fontSize: '0.8rem' }}
                  onClick={() => setEditModal(prev => ({ ...prev, endDate: null }))}
                >
                  Clear end date (mark as current)
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem', color: '#94A3B8' }}>
                  Leave empty for current {label.toLowerCase()}
                </Typography>
              )}
            </Field>
          </Box>
        </Modal>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={deleteModal.open}
          onClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
          title={`Delete ${label} Record`}
          message={`Are you sure you want to delete the ${label.toLowerCase()} record for ${deleteItemPerson?.fullName || 'Unknown'}?`}
          actionButtonText={deleteModal.isDeleting ? 'Deleting...' : 'Delete'}
          actionButtonColor="#DC2626"
        />

        {/* Create Modal */}
        <Modal
          isOpen={createModal.open}
          onClose={handleCloseCreate}
          title={`Add ${label} Record`}
          onSubmit={handleCreateSubmit}
          submitText={createModal.isSubmitting ? 'Creating...' : 'Create'}
          submitDisabled={!createModal.manager || !createModal.startDate || createModal.isSubmitting}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Field>
              <EmployeeSearch
                value={createModal.manager}
                onChange={(employee) => setCreateModal(prev => ({ ...prev, manager: employee }))}
                label={label}
                placeholder={`Search for ${label.toLowerCase()}...`}
                width={400}
                height="56px"
                disabled={createModal.isSubmitting}
                showSelectedAvatar={true}
              />
            </Field>

            <Field>
              <DatePicker
                label="Start Date"
                value={createModal.startDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                    setCreateModal(prev => ({ ...prev, startDate: newValue }));
                  } else if (newValue === null) {
                    setCreateModal(prev => ({ ...prev, startDate: null }));
                  }
                }}
                format="DD/MM/YYYY"
                disabled={createModal.isSubmitting}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    required: true
                  }
                }}
              />
            </Field>

            <Field>
              <DatePicker
                label="End Date"
                value={createModal.endDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                    setCreateModal(prev => ({ ...prev, endDate: newValue }));
                  } else if (newValue === null) {
                    setCreateModal(prev => ({ ...prev, endDate: null }));
                  }
                }}
                format="DD/MM/YYYY"
                disabled={createModal.isSubmitting}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small"
                  }
                }}
              />
              {createModal.endDate ? (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: 'pointer', mt: 1, fontSize: '0.8rem' }}
                  onClick={() => setCreateModal(prev => ({ ...prev, endDate: null }))}
                >
                  Clear end date (mark as current)
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem', color: '#94A3B8' }}>
                  Leave empty for current {label.toLowerCase()}
                </Typography>
              )}
            </Field>
          </Box>
        </Modal>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ManagerHistoryModal;
